import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import styled from '@emotion/styled';
import { Draggable } from 'react-beautiful-dnd';
import Editor from '@monaco-editor/react';
import { MarkdownRenderer } from '@edusense/shared-ui-content';
import InteractiveQuiz from '../InteractiveQuiz/InteractiveQuiz';
import { parseQuizBlockContent } from '../../utils/quizBlockParser';
// Lucide React 직접 import - Vite 번들링 최적화 및 초기화 순서 문제 해결
import { X, Maximize2 } from 'lucide-react';
// 임시 스타일 정의 (실제로는 LectureEditForm/index.tsx의 스타일 상속 또는 별도 파일 관리)
const TempBlockStyles = {
    BlockWithPreviewContainer: styled.div`
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    overflow: hidden;
    margin-bottom: 16px;
    transition: all 0.3s ease;
    box-shadow: ${(props)=>props.$isOpen ? '0 2px 8px rgba(0, 0, 0, 0.05)' : 'none'};
  `,
    BlockHeader: styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background-color: #f9fafb; // 임시 색상
    border-bottom: ${(props)=>props.$isOpen ? `1px solid #e5e7eb` : 'none'};
    cursor: pointer;
  `,
    BlockTypeWrapper: styled.div`display: flex; align-items: center; gap: 6px; font-size: 0.85rem;`,
    DragHandle: styled.div`cursor: grab; display: flex; align-items: center; justify-content: center; width: 28px; height: 28px; color: #6b7280;`,
    BlockTypeLabel: styled.span`display: inline-flex; align-items: center; padding: 2px 6px; border-radius: 4px; font-size: 0.75rem; font-weight: 500; color: white; background-color: #6b7280; margin-right: 6px;`,
    BlockMetadata: styled.span`font-size: 0.7rem; color: #6b7280; margin-left: 4px; max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`,
    BlockSummary: styled.div`font-size: 0.85rem; color: #4b5563; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; margin-left: 8px;`,
    BlockActions: styled.div`display: flex; gap: 4px;`,
    BlockAction: styled.button`background: none; border: none; width: 28px; height: 28px; border-radius: 4px; color: #6b7280; cursor: pointer; transition: all 0.2s; &:hover { background-color: #f3f4f6; color: #1f2937; }`,
    RotateIcon: styled.span`display: inline-flex; align-items: center; justify-content: center; transition: transform 0.3s ease; transform: rotate(${(props)=>props.$isOpen ? '180deg' : '0deg'});`,
    BlockContentWithPreview: styled.div`display: flex; flex-direction: row; border-top: 1px solid #e5e7eb; max-height: ${(props)=>props.$isOpen ? '2000px' : '0px'}; opacity: ${(props)=>props.$isOpen ? '1' : '0'}; visibility: ${(props)=>props.$isOpen ? 'visible' : 'hidden'}; overflow: hidden; transition: max-height 0.5s ease, opacity 0.3s ease, visibility 0s linear ${(props)=>props.$isOpen ? '0s' : '0.5s'};`,
    BlockEditorPane: styled.div`flex: 1; padding: 0; border-right: 1px solid #e5e7eb; display: flex; flex-direction: column; min-height: 50px; overflow: hidden;`,
    BlockPreviewPane: styled.div`
    flex: 1;
    padding: ${(props)=>props.$isOpen ? '12px' : '0'};
    background-color: #f9fafb;
    transition: padding 0.3s ease;
    overflow-y: auto;
  `
};
const HtmlPreview = styled.div`
  border-radius: 8px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  color: #1f2937;
  font-size: 0.9rem;
  overflow-wrap: anywhere;
`;
const TerminalPreview = styled.pre`
  background: #111827;
  color: #e5e7eb;
  border-radius: 8px;
  padding: 12px;
  font-family: 'Fira Code', 'Source Code Pro', monospace;
  font-size: 0.85rem;
  overflow-x: auto;
`;
const PlainPreview = styled.pre`
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.9rem;
  color: #1f2937;
`;
const QuizPreviewNotice = styled.p`
  margin-top: 8px;
  font-size: 0.75rem;
  color: #6b7280;
`;
export const renderBlockPreviewContent = (block)=>{
    const value = block.content || '';
    switch(block.type){
        case 'markdown':
        case 'table':
        case 'image':
            return value ? /*#__PURE__*/ _jsx(MarkdownRenderer, {
                markdown: value
            }) : /*#__PURE__*/ _jsx(PlainPreview, {
                children: "미리볼 내용이 없습니다."
            });
        case 'terminal':
            return /*#__PURE__*/ _jsx(TerminalPreview, {
                children: value || '# 명령을 입력하세요.'
            });
        case 'html':
            return value ? /*#__PURE__*/ _jsx(HtmlPreview, {
                dangerouslySetInnerHTML: {
                    __html: value
                }
            }) : /*#__PURE__*/ _jsx(HtmlPreview, {
                children: "미리볼 HTML이 없습니다."
            });
        case 'quiz':
            {
                const quizQuestions = parseQuizBlockContent(value);
                if (!quizQuestions.length) {
                    return /*#__PURE__*/ _jsx(PlainPreview, {
                        children: `퀴즈 블록 구문을 파싱할 수 없습니다. YAML 형식을 확인해주세요.\n\n예시:\n:::quiz\nid: quiz-1\ntype: multiple-choice\nquestion: 예시 문항\noptions:\n  - 선택지 1\n  - 선택지 2\nanswerIndex: 0\n:::`
                    });
                }
                return /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx(InteractiveQuiz, {
                            quizData: quizQuestions,
                            onSubmit: ()=>undefined
                        }),
                        /*#__PURE__*/ _jsx(QuizPreviewNotice, {
                            children: "미리보기용 컴포넌트입니다. 실제 강의에서는 학습자가 직접 응답합니다."
                        })
                    ]
                });
            }
        default:
            return /*#__PURE__*/ _jsx(PlainPreview, {
                children: value
            });
    }
};
const DraggableBlockItemComponent = /*#__PURE__*/ React.memo(({ block, index, editorOptions, onContentChange, onToggleOpen, onRemove, onEditorMount, getBlockTypeIcon, getBlockTypeLabel, getBlockSummary, onPreviewExpand })=>{
    // 임시 스타일 사용 (실제로는 props로 받거나 import)
    const { BlockWithPreviewContainer, BlockHeader, BlockTypeWrapper, DragHandle, BlockTypeLabel, BlockMetadata, BlockSummary, BlockActions, BlockAction, RotateIcon, BlockContentWithPreview, BlockEditorPane, BlockPreviewPane } = TempBlockStyles;
    const previewNode = React.useMemo(()=>renderBlockPreviewContent(block), [
        block.content,
        block.type,
        block.metadata
    ]);
    return /*#__PURE__*/ _jsx(Draggable, {
        draggableId: block.id,
        index: index,
        children: (providedDraggable)=>/*#__PURE__*/ _jsxs(BlockWithPreviewContainer, {
                ref: providedDraggable.innerRef,
                ...providedDraggable.draggableProps,
                $isOpen: !!block.isOpen,
                children: [
                    /*#__PURE__*/ _jsxs(BlockHeader, {
                        $type: block.type,
                        $isOpen: !!block.isOpen,
                        onClick: ()=>onToggleOpen(block.id),
                        children: [
                            /*#__PURE__*/ _jsxs(BlockTypeWrapper, {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        ...providedDraggable.dragHandleProps,
                                        children: /*#__PURE__*/ _jsx(DragHandle, {
                                            $type: block.type,
                                            children: "≡"
                                        })
                                    }),
                                    getBlockTypeIcon(block.type, block.metadata?.blockName),
                                    /*#__PURE__*/ _jsxs(BlockTypeLabel, {
                                        $type: block.type,
                                        children: [
                                            getBlockTypeLabel(block.type),
                                            block.metadata?.blockName && block.metadata.blockName !== block.type && /*#__PURE__*/ _jsxs(BlockMetadata, {
                                                children: [
                                                    "(",
                                                    block.metadata.blockName,
                                                    ")"
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(BlockSummary, {
                                        children: getBlockSummary(block)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(BlockActions, {
                                children: [
                                    onPreviewExpand && /*#__PURE__*/ _jsx(BlockAction, {
                                        onClick: (e)=>{
                                            e.stopPropagation();
                                            onPreviewExpand(block);
                                        },
                                        title: "미리보기 크게 보기",
                                        children: /*#__PURE__*/ _jsx(Maximize2, {
                                            size: 15
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(BlockAction, {
                                        onClick: (e)=>{
                                            e.stopPropagation();
                                            onToggleOpen(block.id);
                                        },
                                        children: /*#__PURE__*/ _jsx(RotateIcon, {
                                            $isOpen: !!block.isOpen,
                                            children: /*#__PURE__*/ _jsx("svg", {
                                                xmlns: "http://www.w3.org/2000/svg",
                                                width: "16",
                                                height: "16",
                                                viewBox: "0 0 24 24",
                                                fill: "none",
                                                stroke: "currentColor",
                                                strokeWidth: "2",
                                                strokeLinecap: "round",
                                                strokeLinejoin: "round",
                                                children: /*#__PURE__*/ _jsx("polyline", {
                                                    points: "6 9 12 15 18 9"
                                                })
                                            })
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(BlockAction, {
                                        onClick: (e)=>{
                                            e.stopPropagation();
                                            onRemove(block.id);
                                        },
                                        children: /*#__PURE__*/ _jsx(X, {
                                            size: 16
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(BlockContentWithPreview, {
                        $isOpen: !!block.isOpen,
                        style: {
                            minHeight: block.isOpen ? `${block.editorHeight || 200}px` : '0px'
                        },
                        children: [
                            /*#__PURE__*/ _jsx(BlockEditorPane, {
                                children: /*#__PURE__*/ _jsx(Editor, {
                                    height: "100%",
                                    language: block.type === 'markdown' ? 'markdown' : 'plaintext',
                                    value: block.content,
                                    onMount: (editor, monaco)=>onEditorMount(editor, monaco, block.id),
                                    onChange: (value)=>onContentChange(block.id, value),
                                    options: editorOptions,
                                    theme: "vs-light"
                                })
                            }),
                            /*#__PURE__*/ _jsx(BlockPreviewPane, {
                                $isOpen: !!block.isOpen,
                                children: previewNode
                            })
                        ]
                    })
                ]
            })
    });
});
DraggableBlockItemComponent.displayName = "DraggableBlockItem";
export default DraggableBlockItemComponent;
